import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MEDIA } from "@/lib/media";
import { getConsent, hasAdConsent, loadTikTok, saveConsent } from "@/lib/consent";
import CookiePreferences from "@/components/CookiePreferences";

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const [prefs, setPrefs] = useState(false);
  const isAdmin = useLocation().pathname.startsWith("/admin");

  useEffect(() => {
    if (hasAdConsent()) loadTikTok();
    setShow(getConsent() === null);
    const open = () => setPrefs(true);
    window.addEventListener("hb-cookie-settings", open);
    return () => window.removeEventListener("hb-cookie-settings", open);
  }, []);

  const decide = (choice, keepOpen = false) => {
    saveConsent(choice);
    setShow(false);
    if (!keepOpen) setPrefs(false);
  };

  return (
    <>
      {show && !prefs && !isAdmin && (
        <div role="dialog" aria-modal="false" aria-labelledby="cookie-banner-title" aria-describedby="cookie-banner-text"
          className="fixed inset-x-0 bottom-0 z-[90] p-3 sm:p-5 pointer-events-none" data-testid="cookie-banner">
          <div className="pointer-events-auto mx-auto max-w-3xl rounded-3xl bg-[#FFF9EE] border-[3px] border-[#19324A] soft-shadow overflow-hidden animate-in slide-in-from-bottom-6 fade-in duration-500">
            <div className="h-2 bg-[linear-gradient(90deg,#FFD93D_0%,#FF6B9D_50%,#4CC9F0_100%)]" />
            <div className="p-4 sm:p-6 flex gap-4 sm:gap-6 items-start">
              <img src={MEDIA.chewieCutout} alt="" aria-hidden="true" className="hidden sm:block w-24 h-24 object-contain shrink-0 -mb-2 drop-shadow" />
              <div className="flex-1 min-w-0">
                <h2 id="cookie-banner-title" className="font-display font-extrabold text-xl sm:text-2xl text-[#19324A] leading-tight">A Little Cookie Business! 🍪🐾</h2>
                <p id="cookie-banner-text" className="mt-2 text-sm sm:text-[15px] text-[#19324A]/80 leading-relaxed">
                  Chewie & Solo use a few <strong>essential cookies</strong> to run the shop (your bag, sign-in, secure payments). With your OK we'd also like to use
                  <strong> TikTok advertising cookies</strong> to see how our ads are doing. Nothing optional runs until you choose — scrolling isn't a yes.
                  Read our <Link to="/legal/cookie-policy" className="underline font-semibold text-[#FF6B9D]" data-testid="cookie-banner-policy-link">Cookie Policy</Link> and{" "}
                  <Link to="/legal/privacy-policy" className="underline font-semibold text-[#FF6B9D]">Privacy Policy</Link>.
                </p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <button type="button" onClick={() => decide({ advertising: true, analytics: true, method: "accept_all" })}
                    className="min-h-[48px] rounded-full bg-[#FFD93D] text-[#19324A] font-display font-bold px-5 hover:bg-[#ffe066] focus-visible:ring-4 ring-[#4CC9F0]/60 outline-none transition-colors"
                    data-testid="cookie-accept-all">Accept All</button>
                  <button type="button" onClick={() => decide({ advertising: false, analytics: false, method: "reject_optional" })}
                    className="min-h-[48px] rounded-full bg-[#FF6B9D] text-white font-display font-bold px-5 hover:bg-[#ff85ae] focus-visible:ring-4 ring-[#4CC9F0]/60 outline-none transition-colors"
                    data-testid="cookie-reject-optional">Reject Optional</button>
                  <button type="button" onClick={() => setPrefs(true)}
                    className="min-h-[48px] rounded-full bg-white border-2 border-[#19324A] text-[#19324A] font-display font-bold px-4 whitespace-nowrap hover:bg-[#4CC9F0]/15 focus-visible:ring-4 ring-[#4CC9F0]/60 outline-none transition-colors"
                    data-testid="cookie-manage">Manage Preferences</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <CookiePreferences open={prefs} onClose={() => { setPrefs(false); setShow(getConsent() === null); }} onDecide={decide} />
    </>
  );
}
